import { useState, useEffect } from 'react'
import { useLanguage } from '../context/LanguageContext'

const NAVY = '#0b2347'
const GOLD = '#C5A059'

function getYouTubeId(url) {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|shorts\/)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

export default function TrustSection() {
  const { t, langMeta } = useLanguage()
  const FONT = `"${langMeta.font}", sans-serif`
  const [testimonials, setTestimonials] = useState([])
  const [caseStudies, setCaseStudies] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      fetch('/api/admin/testimonials').then(r => r.json()).catch(() => []),
      fetch('/api/admin/casestudies').then(r => r.json()).catch(() => [])
    ]).then(([tData, cData]) => {
      setTestimonials(tData)
      setCaseStudies(cData)
      setLoading(false)
    })
  }, [])

  if (loading || (testimonials.length === 0 && caseStudies.length === 0)) return null

  return (
    <div style={{ background: '#fff', padding: '80px 20px', fontFamily: FONT }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        
        {/* Video Testimonials */}
        {testimonials.length > 0 && (
          <div style={{ marginBottom: '80px' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <span style={{ color: GOLD, fontSize: '12px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>{t('trust.clientStories')}</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: NAVY, margin: 0 }}>{t('trust.hearFromClients')}</h2>
            </div>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px', justifyContent: 'center' }}>
              {testimonials.map((tItem, i) => {
                const ytId = getYouTubeId(tItem.link)
                return (
                  <div key={i} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 20px 40px -10px rgba(0,46,83,0.08)', display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '420px', transition: 'transform 0.3s ease', ':hover': { transform: 'translateY(-5px)' } }}>
                    
                    {/* Floating Video Player */}
                    <div style={{ padding: '16px 16px 0 16px' }}>
                      <div style={{ borderRadius: '16px', overflow: 'hidden', width: '100%', paddingTop: '56.25%', position: 'relative', background: '#0f172a', boxShadow: '0 8px 20px rgba(0,0,0,0.1)' }}>
                        {ytId ? (
                          <iframe 
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                            src={`https://www.youtube.com/embed/${ytId}`} 
                            title={tItem.clientName} frameBorder="0" 
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                            allowFullScreen 
                          />
                        ) : (
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', color: '#94a3b8', textAlign: 'center', fontStyle: 'italic', fontSize: '14px' }}>{t('trust.videoUnavailable')}</div>
                        )}
                      </div>
                    </div>

                    {/* Premium Content Area */}
                    <div style={{ padding: '28px 24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                        <div>
                          <h3 style={{ fontSize: '20px', fontWeight: 800, color: NAVY, margin: '0 0 6px', letterSpacing: '-0.3px' }}>{tItem.clientName}</h3>
                          {tItem.location && (
                            <span style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', fontWeight: 500 }}>
                              {tItem.location}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '2px', background: '#f8fafb', padding: '6px 10px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                          {[...Array(5)].map((_, index) => (
                            <span key={index} style={{ color: index < (Number(tItem.rating) || 5) ? GOLD : '#e2e8f0', fontSize: '16px' }}>★</span>
                          ))}
                        </div>
                      </div>

                      {tItem.quote && (
                        <div style={{ position: 'relative', marginTop: 'auto', background: '#f8fafb', padding: '24px', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" style={{ position: 'absolute', top: '-12px', left: '16px', color: GOLD, background: '#fff', padding: '0 4px' }}>
                            <path d="M14.017 21L16.41 14.286H12V3H21V11.571L18.423 21H14.017ZM5.017 21L7.41 14.286H3V3H12V11.571L9.423 21H5.017Z" />
                          </svg>
                          <p style={{ fontSize: '15px', color: '#475569', margin: '4px 0 0', lineHeight: 1.6, fontStyle: 'italic', fontWeight: 500 }}>"{tItem.quote}"</p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Case Studies */}
        {caseStudies.length > 0 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <span style={{ color: GOLD, fontSize: '12px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>{t('trust.trackRecord')}</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: NAVY, margin: 0 }}>{t('trust.provenSuccess')}</h2>
            </div>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '30px', justifyContent: 'center' }}>
              {caseStudies.map((c, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,46,83,0.08)', display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '380px' }}>
                  {c.coverImage ? (
                    <div style={{ width: '100%', height: '220px', backgroundImage: `url(${c.coverImage})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,46,83,0.8), transparent)' }} />
                      <h3 style={{ position: 'absolute', bottom: '20px', left: '24px', right: '24px', color: '#fff', margin: 0, fontSize: '20px', fontWeight: 700, lineHeight: 1.3 }}>{c.title}</h3>
                    </div>
                  ) : (
                    <div style={{ padding: '24px 24px 0', background: NAVY, color: '#fff' }}>
                      <h3 style={{ fontSize: '20px', fontWeight: 700, margin: 0, lineHeight: 1.3 }}>{c.title}</h3>
                    </div>
                  )}
                  
                  <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {c.result && (
                      <div style={{ display: 'inline-block', padding: '6px 12px', background: 'rgba(16, 185, 129, 0.1)', color: '#059669', borderRadius: '6px', fontSize: '13px', fontWeight: 700, alignSelf: 'flex-start', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                        {c.result}
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {c.challenge && (
                        <div>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>The Challenge</span>
                          <p style={{ fontSize: '14px', color: '#475569', margin: '4px 0 0', lineHeight: 1.5 }}>{c.challenge}</p>
                        </div>
                      )}
                      {c.strategy && (
                        <div style={{ paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>The Strategy</span>
                          <p style={{ fontSize: '14px', color: '#475569', margin: '4px 0 0', lineHeight: 1.5 }}>{c.strategy}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
